import React, { useEffect, useState } from 'react';
import { parseServerTime } from '@/lib/serverTime';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import axios from 'axios';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, AlertCircle, FileText } from 'lucide-react';
import { API_BASE_URL } from '@/api/apiConfig';

interface AuditLog {
  id: number;
  userId: string;
  action: string;
  targetKey: string | null; // 관련 리소스 식별자 (API Key, 사용자 ID 등)
  timestamp: string; // 액션 수행 일시 (백엔드 가이드 기준)
  performedBy: string;
  ipAddress?: string; // IP 주소
}

// 백엔드 가이드에서 정의한 Action 타입
const ACTION_TYPES = [
  { value: '', label: '전체 행위' },
  { value: 'API_KEY_ISSUED', label: 'API Key 발급' },
  { value: 'API_KEY_REVOKED', label: 'API Key 폐기' },
  { value: 'USER_CREATED', label: '사용자 생성' },
  { value: 'USER_UPDATED', label: '사용자 정보 수정' },
  { value: 'USER_DELETED', label: '사용자 삭제' },
  { value: 'USER_APPROVED', label: '사용자 승인' },
  { value: 'USER_REJECTED', label: '사용자 거부' },
  { value: 'PASSWORD_CHANGED', label: '비밀번호 변경' },
  { value: 'PASSWORD_RESET', label: '비밀번호 재설정' },
  { value: 'LOGIN_SUCCESS', label: '로그인 성공' },
  { value: 'LOGIN_FAILED', label: '로그인 실패' },
  { value: 'LOGOUT', label: '로그아웃' },
  { value: 'TEAM_INVITE_MEMBER', label: '팀 멤버 초대' },
  { value: 'TEAM_ROLE_CHANGE', label: '팀 역할 변경' },
  { value: 'TEAM_REMOVE_MEMBER', label: '팀 멤버 제거' },
  { value: 'EXTENSION_ERROR', label: '확장프로그램 에러' },
];

// Action별 Badge 색상
const getActionBadge = (action: string) => {
  const styles: Record<string, string> = {
    'API_KEY_ISSUED': 'bg-blue-100 text-blue-800',
    'API_KEY_REVOKED': 'bg-gray-200 text-gray-700',
    'USER_CREATED': 'bg-green-100 text-green-800',
    'USER_UPDATED': 'bg-yellow-100 text-yellow-800',
    'USER_DELETED': 'bg-red-100 text-red-800',
    'USER_APPROVED': 'bg-green-100 text-green-800',
    'USER_REJECTED': 'bg-red-100 text-red-800',
    'PASSWORD_CHANGED': 'bg-purple-100 text-purple-800',
    'PASSWORD_RESET': 'bg-purple-100 text-purple-800',
    'LOGIN_SUCCESS': 'bg-blue-100 text-blue-800',
    'LOGIN_FAILED': 'bg-red-100 text-red-800',
    'LOGOUT': 'bg-gray-200 text-gray-700',
    'TEAM_INVITE_MEMBER': 'bg-teal-100 text-teal-800',
    'TEAM_ROLE_CHANGE': 'bg-indigo-100 text-indigo-800',
    'TEAM_REMOVE_MEMBER': 'bg-orange-100 text-orange-800',
    'EXTENSION_ERROR': 'bg-red-100 text-red-800',
  };
  
  const label = ACTION_TYPES.find(a => a.value === action)?.label || action;
  const className = styles[action] || 'bg-gray-100 text-gray-800';
  
  return <Badge className={className}>{label}</Badge>;
};

const API_BASE = `${API_BASE_URL}/api/admin/audit-logs`;

export default function AdminAuditLogs() {
  const { user } = useAuth();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // DateTime 형식 변환 함수 (ISO 8601 형식)
  const formatDateTime = (dateStr: string): string => {
    if (!dateStr || !dateStr.trim()) return '';
    // yyyy-MM-dd 형식을 yyyy-MM-ddT00:00:00 형식으로 변환
    const trimmed = dateStr.trim();
    // 이미 시간 정보가 있으면 그대로 반환
    if (trimmed.includes('T')) return trimmed;
    // 날짜만 있으면 00:00:00 추가
    return `${trimmed}T00:00:00`;
  };

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      // PostgreSQL 타입 추론 오류 방지: undefined를 사용하여 axios가 자동으로 제외하도록 함
      const params: any = {
        page,
        size,
        userId: (userId && userId.trim()) ? userId.trim() : undefined,
        action: (action && action.trim()) ? action : undefined,
        startDate: (startDate && startDate.trim()) ? formatDateTime(startDate) : undefined,
        endDate: (endDate && endDate.trim()) ? `${endDate}T23:59:59` : undefined,
      };
      
      // undefined 값을 가진 속성 제거 (추가 안전장치)
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === null || params[key] === '') {
          delete params[key];
        }
      });
      
      // 디버깅용: 실제 전송되는 파라미터 확인
      console.log('🔍 Audit Log API 호출 파라미터:', params);
      
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const res = await axios.get(API_BASE, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params,
      });
      
      // 백엔드 응답 구조에 맞게 처리
      const data = res.data.data || res.data;
      setLogs(data.content || data.logs || data || []);
      setTotalElements(data.totalElements || res.data.totalElements || 0);
      setTotalPages(data.totalPages || res.data.totalPages || Math.ceil((data.totalElements || 0) / size));
    } catch (e: any) {
      if (e.response?.status === 403) {
        setError('관리자 권한이 필요합니다.');
      } else {
        setError(e.response?.data?.message || '감사 로그 조회 실패');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    // SUPER_ADMIN만 접근 가능
    if (user?.userRole !== 'SUPER_ADMIN') {
      setError('이 페이지는 수퍼 관리자만 접근할 수 있습니다.');
      return;
    }
    fetchLogs();
    // eslint-disable-next-line
  }, [page, size]);

  const handleSearch = () => {
    setPage(0);
    fetchLogs();
  };

  const handleReset = () => {
    setUserId('');
    setAction('');
    setStartDate('');
    setEndDate('');
    setPage(0);
    setTimeout(() => fetchLogs(), 100);
  };

  // 관리자 권한 체크
  if (user?.userRole !== 'SUPER_ADMIN') {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            이 페이지는 수퍼 관리자만 접근할 수 있습니다.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || '감사 로그 관리'}
        description="시스템 내 모든 중요 작업에 대한 감사 로그 조회"
        icon={menuIcon}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-4 text-sm hover:underline">
              닫기
            </button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>검색 필터</CardTitle>
          <CardDescription>사용자 ID, 행위 유형, 날짜 범위로 필터링</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input 
              placeholder="사용자 ID" 
              value={userId} 
              onChange={e => setUserId(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
            />
            <select
              value={action}
              onChange={e => setAction(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {ACTION_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <Input 
              type="date" 
              placeholder="시작일"
              value={startDate} 
              onChange={e => setStartDate(e.target.value)} 
            />
            <Input 
              type="date" 
              placeholder="종료일"
              value={endDate} 
              onChange={e => setEndDate(e.target.value)} 
            />
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={handleSearch} disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              검색
            </Button>
            <Button onClick={handleReset} variant="outline" disabled={loading}>
              초기화
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <CardTitle>감사 로그 목록</CardTitle>
              <CardDescription>전체 {totalElements.toLocaleString()}건</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">페이지 크기:</span>
              <select 
                value={size} 
                onChange={e => { 
                  setSize(Number(e.target.value)); 
                  setPage(0); 
                }}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>일시</TableHead>
                  <TableHead>행위</TableHead>
                  <TableHead>사용자 ID</TableHead>
                  <TableHead>에러 상세 메시지</TableHead>
                  <TableHead>확장프로그램 버전</TableHead>
                  {logs.some(log => log.ipAddress) && <TableHead>IP 주소</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                      <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>로딩 중...</p>
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                      <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>조회된 로그가 없습니다</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {parseServerTime(log.timestamp)?.toLocaleString('ko-KR')}
                      </TableCell>
                      <TableCell>
                        {getActionBadge(log.action)}
                      </TableCell>
                      <TableCell className="font-medium">{log.userId}</TableCell>
                      <TableCell className="text-xs max-w-md">
                        {log.targetKey || '-'}
                      </TableCell>
                      <TableCell className="text-xs">{log.performedBy || '-'}</TableCell>
                      {logs.some(l => l.ipAddress) && <TableCell className="text-xs">{log.ipAddress || '-'}</TableCell>}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <Button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                variant="outline"
              >
                이전
              </Button>
              <span className="px-4 py-2 text-sm">
                {page + 1} / {totalPages}
              </span>
              <Button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
                variant="outline"
              >
                다음
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
